import styled from 'styled-components';
import {
  InfoWindowContainerProps,
  PrimaryPosition,
  SecondaryAlignment,
  INFO_WINDOW_CONSTANTS,
} from './MSmartInfoWindow.types';

const { WIDTH, MARKER_HEIGHT, MARKER_TAIL_HEIGHT, DOT_MARKER_SIZE, GAP } = INFO_WINDOW_CONSTANTS;

/**
 * Calculate the transform based on primary position, secondary alignment, and dynamic marker width.
 *
 * For price pill markers:
 * - The marker's pixel position is at the lat/lng anchor point (center-bottom of marker body)
 * - The tail extends 5px below the marker body
 *
 * For dot markers (single property):
 * - The marker is an 18px circle with no tail
 * - The anchor is at the center of the circle
 */
const getTransform = (
  primary: PrimaryPosition,
  secondary: SecondaryAlignment,
  markerHalfWidth: number,
  isSinglePropertyResponse: boolean
): string => {
  // Dot marker: 18px circle, anchor at center, no tail
  // Price pill: 20px body + 5px tail, anchor at body bottom
  const markerHeight = isSinglePropertyResponse ? DOT_MARKER_SIZE / 2 : MARKER_HEIGHT;

  switch (primary) {
    case 'top': {
      // Must clear marker height plus tail (if any) plus gap
      // For dot marker: anchor at bottom of circle, need to clear full dot (18px) + gap (4px) + extra (4px) = 26px
      // For price pill: anchor at body bottom, need to clear body (20px) + tail (5px) + gap (4px) = 29px
      const verticalOffset = isSinglePropertyResponse
        ? `calc(-100% - ${DOT_MARKER_SIZE + GAP + 4}px)`
        : `calc(-100% - ${MARKER_HEIGHT + MARKER_TAIL_HEIGHT + GAP}px)`;
      switch (secondary) {
        case 'start':
          return `translate(-${markerHalfWidth}px, ${verticalOffset})`;
        case 'end':
          return `translate(calc(-100% + ${markerHalfWidth}px), ${verticalOffset})`;
        case 'center':
        default:
          return `translate(-50%, ${verticalOffset})`;
      }
    }

    case 'bottom': {
      // For dot marker: anchor is at center, so offset by half height + gap
      // For price pill: anchor is at body bottom, tail extends below, then add gap
      const verticalOffset = isSinglePropertyResponse
        ? `${DOT_MARKER_SIZE / 2 + GAP}px`
        : `${MARKER_TAIL_HEIGHT + GAP + 4}px`;
      switch (secondary) {
        case 'start':
          return `translate(-${markerHalfWidth}px, ${verticalOffset})`;
        case 'end':
          return `translate(calc(-100% + ${markerHalfWidth}px), ${verticalOffset})`;
        case 'center':
        default:
          return `translate(-50%, ${verticalOffset})`;
      }
    }

    case 'left': {
      const horizontalOffset = `calc(-100% - ${markerHalfWidth + GAP + 2}px)`;
      switch (secondary) {
        case 'start':
          return `translate(${horizontalOffset}, -${markerHeight}px)`;
        case 'end':
          return `translate(${horizontalOffset}, calc(-100% + ${markerHeight}px))`;
        case 'center':
        default:
          return `translate(${horizontalOffset}, -50%)`;
      }
    }

    case 'right': {
      const horizontalOffset = `${markerHalfWidth + GAP + 2}px`;
      switch (secondary) {
        case 'start':
          return `translate(${horizontalOffset}, -${markerHeight}px)`;
        case 'end':
          return `translate(${horizontalOffset}, calc(-100% + ${markerHeight}px))`;
        case 'center':
        default:
          return `translate(${horizontalOffset}, -50%)`;
      }
    }

    default:
      return 'translate(-50%, calc(-100% - 32px))';
  }
};

/**
 * Main container for the smart InfoWindow
 *
 * Rendered via React portal into a positioned ancestor of the map (e.g.
 * `ChatMapContainer`). Positioned absolutely using marker pixel coordinates
 * so it escapes Google Maps' overlay pane stacking context and can render
 * above markers, clusters, filter chips, and the list toggle.
 */
export const InfoWindowContainer = styled.div<InfoWindowContainerProps>`
  position: absolute;
  top: ${props => props.$markerPixelY}px;
  left: ${props => props.$markerPixelX}px;
  width: ${WIDTH}px;
  transform: ${props =>
    getTransform(props.$primary, props.$secondary, props.$markerHalfWidth, props.$isSinglePropertyResponse ?? false)};
  pointer-events: auto;
  z-index: 1500;
`;
